import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Footerbar from './components/Footerbar';
import useAppLayout from './hooks/useAppLayout';
import API_URL from './api/config';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast, ToastContainer } from 'react-toastify';
import ReactModal from 'react-modal';
import { Link, useNavigate } from 'react-router-dom';
import imageCompression from 'browser-image-compression';

// --- Global OrgChart script loader (only loads once for the application) ---
let orgChartScriptLoaded = false;
let orgChartLoadPromise = null;

const loadOrgChartScript = (src) => {
  if (orgChartScriptLoaded) {
    return Promise.resolve();
  }
  if (orgChartLoadPromise) {
    return orgChartLoadPromise;
  }

  orgChartLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => {
      orgChartScriptLoaded = true;
      resolve();
    };
    script.onerror = reject;
    document.body.appendChild(script);
  });

  return orgChartLoadPromise;
};
// --- End Global OrgChart script loader ---


function FamilySettingsTreeBackground() {
  const navigate = useNavigate();

  const loggedInUserId = localStorage.getItem("user_id");
  const loggedFamilyAdmin = localStorage.getItem("family_admin");

  const { isSidebarVisible, showSidebar, toggleSidebar } = useAppLayout();
  const chartContainerRef = useRef(null);
  const chartInstanceRef = useRef(null); // Ref to store the OrgChart instance

  const queryClient = useQueryClient();

  const getToken = () => localStorage.getItem('token');

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState({
    id: '', name: '', alt_name: '', img: '', gender: '',
    married: '', single: '', single_mother: '', spouse: '', unknown: '',
  });

  // New state for chart-specific loading
  const [isChartLoading, setIsChartLoading] = useState(true); // Default to true to show spinner initially
  const [showSpinner, setShowSpinner] = useState(false); // State to control spinner visibility with delay

  const openModalWithData = (data) => {
    setSelectedMember({
      id: data.id || '',
      name: data.name || '',
      alt_name: data.alt_name || '',
      img: data.img || '',
      gender: data.gender || '',
      married: data.married || '',
      single: data.single || '',
      single_mother: data.single_mother || '',
      spouse: data.spouse || '',
      unknown: data.unknown || '',
    });
    setModalIsOpen(true);
  };

  // Query to fetch family tree data
  const { data: familyTreeData, isLoading: isLoadingFamilyTree, isError: isErrorFamilyTree } = useQuery({
    queryKey: ['familyTree'],
    queryFn: () =>
      axios
        .get(`${API_URL}/family-tree`, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        })
        .then((res) => res.data),
    staleTime: 0, // Data is immediately stale and will be refetched if re-rendered/accessed
    cacheTime: 10 * 60 * 1000, // Keep data in cache for 10 minutes
  });

  // Handle spinner visibility with a delay to prevent flicker
  useEffect(() => {
    let timer;
    // Show spinner if either data is loading or chart is rendering, and only after 500ms
    if (isLoadingFamilyTree || isChartLoading) {
      timer = setTimeout(() => setShowSpinner(true), 500);
    } else {
      setShowSpinner(false); // Hide spinner immediately when not loading
    }
    return () => clearTimeout(timer); // Clear timer if state changes before 500ms
  }, [isLoadingFamilyTree, isChartLoading]);


  // Load OrgChart script and initialize/update chart when data is available
  useEffect(() => {
    if (!familyTreeData) {
      setIsChartLoading(true); // Keep chart loading state true until data is there
      return;
    }

    // Set chart loading to true while we prepare to render/update it
    setIsChartLoading(true);

    loadOrgChartScript(`${process.env.PUBLIC_URL}/orgchart/orgchart.js`)
      .then(() => {
        if (window.OrgChart) {
          initializeOrgChart(familyTreeData);
        }
      })
      .catch((error) => {
        console.error('Failed to load OrgChart script:', error);
        setIsChartLoading(false); // If script fails, stop loading
      });

    // Cleanup function for OrgChart instance
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [familyTreeData]); // Runs when familyTreeData updates


  const initializeOrgChart = (data) => {
    if (!window.OrgChart || !chartContainerRef.current) {
      setIsChartLoading(false); // Ensure loading is off if requirements not met
      return;
    }

    // This part is crucial: ALWAYS set loading to true when you start loading/updating the chart
    // and then rely on the 'ready' event to set it to false.
    setIsChartLoading(true);

    if (chartInstanceRef.current) {
      // If an instance already exists, load new data into it.
      // The 'ready' event listener is already attached or will be re-attached below.
      chartInstanceRef.current.load(data);
    } else {
      // Create a new instance if one doesn't exist
      const isMobile = window.innerWidth <= 768;
      const OrgChart = window.OrgChart;

      OrgChart.templates.family_template = Object.assign({}, OrgChart.templates.ana);
      OrgChart.templates.family_template.size = [86, 86];
      OrgChart.templates.family_template.plus = '';
      OrgChart.templates.family_template.minus = '';
      OrgChart.templates.family_template.rippleRadius = 40;
      OrgChart.templates.family_template.name = '<text style="font-size: 12px;" fill="#000000" x="43" y="100" text-anchor="middle">{val}</text>';
      OrgChart.templates.family_template.title = '<text style="font-size: 12px;" fill="#aeaeae" x="43" y="115" text-anchor="middle">{val}</text>';
    OrgChart.templates.family_template.img = '<clipPath id="{randId}"><circle cx="43" cy="43" r="40.5"></circle></clipPath><image preserveAspectRatio="xMidYMid slice" clip-path="url(#{randId})" xlink:href="{val}" x="2" y="2" width="82" height="82"></image>';
      OrgChart.templates.family_template.node = '<circle stroke-width="1" fill="#FF1493" stroke="#FF1493" cx="43" cy="43" r="41.5"></circle>';

      OrgChart.templates.family_template_blue = Object.assign({}, OrgChart.templates.family_template);
      OrgChart.templates.family_template_blue.node = '<circle stroke-width="1" fill="#2C533D" stroke="#2C533D" cx="43" cy="43" r="41.5"></circle>';

      const chart = new OrgChart(chartContainerRef.current, {
        template: 'family_template',
        enableSearch: false,
        levelSeparation: isMobile ? 60 : 100,
        siblingSeparation: isMobile ? 60 : 100,
        scaleInitial: isMobile ? OrgChart.match.boundary : 1,
        scaleMin: 0.5,
        scaleMax: 3,
        nodeBinding: {
          name: 'name',
          title: 'title',
          img: 'img',
        },
        tags: {
          blue: { template: 'family_template_blue' },
          partner: { template: 'family_template' },
        },
        nodeMouseClick: OrgChart.action.none, // override default popup
      });

      chart.on('render-link', (sender, args) => {
        
        if (args.cnode.ppid !== undefined) {
          args.html += '<use xlink:href="#baby" x="' + args.p.xa + '" y="' + args.p.ya + '"/>';
        }
      });

      chart.on('redraw', function () {
        const chartElement = document.querySelector('#tree svg');
        if (chartElement) {
          chartElement.style.touchAction = 'pan-x pan-y';
        }
      });

      chart.on('click', (sender, args) => {
        if (args && args.node) {
          const nodeData = sender.get(args.node.id);
          setTimeout(() => {
            openModalWithData(nodeData);
          }, 150); // Slight delay fixes the flash
        }
      });

      chart.load(data);
      chartInstanceRef.current = chart;
    }

    // Ensure the 'ready' event listener is always attached/re-attached
    // This will set isChartLoading to false once the chart has finished rendering
    if (chartInstanceRef.current) {
        // Remove previous listener to prevent multiple calls if initializeOrgChart runs again
        // This is good practice if you re-attach listeners on every render
        chartInstanceRef.current.off('ready');
        chartInstanceRef.current.on('ready', () => {
            setIsChartLoading(false);
        });
    }
  };

  // Existing state and effects for modals, relationships, etc. (unchanged)
 



  const { data: treeExpired, isLoadingExpiry } = useQuery({
    queryKey: ["userExpiryDetails"],
    queryFn: () =>
      axios.get(`${API_URL}/user-payment-details-api`, { withCredentials: true })
        .then((res) => res.data),
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });
  
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  
  useEffect(() => {
    if (!isLoadingExpiry && treeExpired) {
      if (treeExpired.expired) {
        setShowSubscribeModal(true);
      } else {
        setShowSubscribeModal(false);
      }
    }
  }, [treeExpired, isLoadingExpiry]);
  
    

     const [viewBackgroundColorModalOpen, setBackgroundColorModalOpen] = useState(false);
     const [colorCode, setColorCode] = useState("#EBF4ED");
     const [loading, setLoading] = useState(false);


     const handleUpdateBackground = async () => {
      if (!colorCode) {
        toast.error("Please select a color");
        return;
      }
    
      setLoading(true);
    
      try {
        const response = await axios.put(
          `${API_URL}/update-family-tree-background-api`,
          { color_code: colorCode },
          { withCredentials: true }
        );
    
        toast.success(
          response.data.message || "Background color updated successfully",
          { position: "top-right", autoClose: 3000 }
        );
    
        setBackgroundColorModalOpen(false);
        fetchTreeBackground();
      } catch (error) {
        toast.error("Error updating background color");
        console.error(error);
      } finally {
        setLoading(false);
      }
    };


    const [treeBgColor, setTreeBgColor] = useState("#EBF4ED");

    const fetchTreeBackground = async () => {
      try {
        const res = await axios.get(
          `${API_URL}/get-family-tree-background-api`,
          { withCredentials: true }
        );
    
        if (res.data?.color_code) {
          setTreeBgColor(res.data.color_code);
        }
      } catch (error) {
        console.error("Failed to load tree background color", error);
      }
    };

    useEffect(() => {
      fetchTreeBackground();
    }, []);


    
  if (isErrorFamilyTree) {
    return <p style={{ textAlign: 'center', color: 'red' }}>Error loading family tree data.</p>;
  }


  return (
    <div className="scrollPageContainer theme-light">
      <div className="main-wrapper" >
        {isSidebarVisible && <Sidebar />}
        <Header toggleSidebar={toggleSidebar} />
        <Sidebar show={showSidebar} toggle={toggleSidebar} />
        <Footerbar toggleSidebar={toggleSidebar} />
        <ToastContainer />

        <div className="page-wrapper" style={{ zIndex: 500,}}>
          <div className="content container-fluid" style={{ zIndex: 500,}}>


             
            
            {/* OrgChart Container - its visibility/opacity is controlled by the spinner */}
            <div
              id="tree"
              ref={chartContainerRef}
              style={{
                width: '100%',
                height: '600px',
                backgroundColor: treeBgColor,
                opacity: showSpinner ? 0 : 1, // Chart is hidden when spinner is shown
                transition: 'opacity 0.5s ease-in-out' // Smooth transition for showing/hiding chart
              }}>
            </div>


            <div className='row' style={{
              position: "absolute",
              bottom: "50px",
              left: 0,
              right: 0,
              zIndex: 10,
              pointerEvents: "none",
            }}>
      <div className='col-12 col-md-12' style={{ display: "flex", justifyContent: "center" }}>

    
      <button  
       onClick={() => setBackgroundColorModalOpen(true)}
      class="btn btn-primary btn-sm"
      style={{ pointerEvents: "auto" }}
      >Update Background Color</button>

      </div>
      
  </div>

            {/* Loading overlay with dimmed background */}
            {showSpinner && (
                
              <div className="loadingOverlay" style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                flexDirection: 'column', // Changed to column to stack spinner and text
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.8)', // Semi-transparent background
                zIndex: 1000 // Ensure it's on top
              }}>
                <div className='loadingspinner'></div>
                <p style={{ textAlign: 'center', color: '#000', marginTop: '10px' }}>Loading tree...</p> {/* Changed text color for better contrast */}
              </div>
            )}



<ReactModal
  isOpen={viewBackgroundColorModalOpen}
  onRequestClose={() => setBackgroundColorModalOpen(false)}
  className="modern-modal tree-bg-modal"
  overlayClassName="modern-overlay"
>
  <div className="modal-container">
    <div className="modal-header-modern">
      <h5 className="modal-title-modern">Tree Background Colors</h5>
      <button
        type="button"
        className="modal-close-btn"
        onClick={() => setBackgroundColorModalOpen(false)}
        aria-label="Close"
      >
        <i className="fas fa-times"></i>
      </button>
    </div>

    <div className="modal-body-modern">
      <label className="form-label-modern">Choose a color</label>
      <select
        name="color_code"
        className="form-input-modern"
        value={colorCode}
        onChange={(e) => setColorCode(e.target.value)}
      >
        <option value="#EBF4ED">Default</option>
        <option value="#FFEBEE">Red</option>
        <option value="#FCE4EC">Pink</option>
        <option value="#F3E5F5">Purple</option>
        <option value="#E8EAF6">Indigo</option>
        <option value="#E3F2FD">Blue</option>
        <option value="#E0F7FA">Cyan</option>
        <option value="#E0F2F1">Teal</option>
        <option value="#E8F5E9">Green</option>
        <option value="#F9FBE7">Lime</option>
        <option value="#FFFDE7">Yellow</option>
        <option value="#FFF3E0">Orange</option>
        <option value="#EFEBE9">Brown</option>
        <option value="#FAFAFA">Grey</option>
      </select>

      <div className="color-preview-card">
        <div className="color-preview-swatch" style={{ backgroundColor: colorCode }} />
        <div className="color-preview-text">Preview</div>
      </div>
    </div>

    <div className="modal-footer-modern">
      <button
        className="btn btn-primary-modern"
        onClick={handleUpdateBackground}
        disabled={loading}
      >
        {loading ? "Updating..." : "Update Background Color"}
      </button>
    </div>
  </div>
</ReactModal>


          
          </div>
        </div>
      </div>


      <style jsx>{`
      
      :root{
        --brand: #75B437;
        --brand-600: #5fa12f;
        --brand-700: #4d8e29;
        --brand-dark: #184A2C;
        --ink: #000000;
        --bg: #EBF4ED;
        --bg-soft: #f4faf6;
      }

     
      .theme-light, .scrollPageContainer { background: var(--bg); }

  
      .modern-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.35);
        backdrop-filter: blur(8px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9999;
        padding: 20px;
        opacity: 0;
        animation: fadeIn 0.2s ease-out forwards;
        box-sizing: border-box;
        overflow-y: auto;
        min-height: 100vh;
      }
      @keyframes fadeIn { to { opacity: 1; } }
 .modern-modal { 
        background: transparent; 
        border: 0; 
        border-radius: 0; 
        box-shadow: none; 
        outline: none; 
        position: relative; 
        width: auto;
        max-width: none; 
        max-height: none; 
        overflow: visible; 
        margin: auto;
      }

      .modal-container{
        background: linear-gradient(145deg, #ffffff 0%, var(--bg-soft) 100%);
        border-radius: 24px;
        box-shadow: 0 25px 50px -12px rgba(0,0,0,.25), 0 0 0 1px rgba(255,255,255,.8);
        position: relative; 
        overflow: hidden;
        transform: scale(.95) translateY(20px);
        animation: modalSlideIn .3s ease-out forwards;
        backdrop-filter: blur(16px);
        max-height: calc(100vh - 40px);
        overflow-y: auto;
        margin: auto;
      }
      @keyframes modalSlideIn { to { transform: scale(1) translateY(0); } }

      .profile-modal .modal-container { width: 400px; max-width: 90vw; height: 600px; max-height: 85vh; display: flex; flex-direction: column; }
      .profile-image-section { flex: 1; position: relative; overflow: hidden; border-radius: 24px 24px 0 0; }
      .large-profile-image-wrapper{ width:100%; height:100%; position:relative; background: linear-gradient(135deg, var(--brand) 0%, var(--brand-dark) 100%); }
      .large-profile-image{ width:100%; height:100%; object-fit:cover; display:block; }
      .large-profile-image-placeholder{ width:100%; height:100%; display:flex; align-items:center; justify-content:center; color: rgba(255,255,255,.9); background: linear-gradient(135deg, var(--brand) 0%, var(--brand-dark) 100%); }
      .image-overlay-info{ position:absolute; left:0; right:0; bottom:0; background: linear-gradient(transparent, rgba(0,0,0,.75)); padding:40px 20px 20px; text-align:center; }
      .overlay-name{ font-size:28px; font-weight:700; margin:0; color:#fff; text-shadow:0 2px 8px rgba(0,0,0,.5); }





      .add-family-modal .form-input-modern {
  height: 36px !important;      
  padding: 0.25rem 0.5rem !important;
  font-size: 0.85rem !important; 
}
.add-family-modal .form-label-modern {
  margin-bottom: 0.25rem !important; 
  font-size: 0.8rem;
}


      .compact-action-buttons{
        padding:16px 20px; display:flex; gap:8px;
        background: rgba(248, 250, 252, .95);
        backdrop-filter: blur(10px);
        border-radius: 0 0 24px 24px;
        border-top:1px solid rgba(226,232,240,.8);
      }
      .btn-compact{ flex:1; display:flex; align-items:center; justify-content:center; gap:6px; padding:10px 8px; border-radius:10px; font-weight:600; font-size:12px; border:none; cursor:pointer; transition:.2s; outline:none; min-height:44px; }

      .btn-add{
        background: linear-gradient(135deg, var(--brand-dark) 0%, #0f3320 100%);
        color:#fff; box-shadow: 0 2px 8px rgba(24,74,44,.35);
      }
      .btn-add:hover{ transform: translateY(-1px); box-shadow: 0 4px 12px rgba(24,74,44,.45); }

      .btn-edit{
        background: transparent; color: var(--brand-dark);
        border: 2px solid var(--brand);
      }
      .btn-edit:hover{ background: var(--brand); color:#fff; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(117,180,55,.35); }

      .btn-profile{
        background: linear-gradient(135deg, var(--brand) 0%, var(--brand-dark) 100%);
        color:#fff; box-shadow: 0 2px 8px rgba(117,180,55,.35);
      }
      .btn-profile:hover{ transform: translateY(-1px); box-shadow: 0 4px 12px rgba(24,74,44,.45); }

   
      .modal-close-btn{
        position:absolute; top:16px; right:16px; width:36px; height:36px;
        background: rgba(255,255,255,.95); border:0; border-radius:50%;
        display:flex; align-items:center; justify-content:center; cursor:pointer;
        color:#475569; transition:.2s; z-index:10; backdrop-filter: blur(10px);
      }
      .modal-close-btn:hover{ background:#fff; color:#ef4444; transform: scale(1.1); }

  
      .add-fa3mily-modal .modal-container {
  width: 420px;       
  max-width: 92vw;   
  max-height: 72vh;    
}

      .modal-header-modern{ padding:24px 30px; border-bottom:1px solid #e2e8f0; display:flex; align-items:center; justify-content:space-between; background: linear-gradient(135deg, #f5faf7 0%, #e6f0ea 100%); }
      .modal-title-modern{ font-size:20px; font-weight:600; color:#1e293b; margin:0; }
      .highlight-name{ color: var(--brand-dark); font-weight:700; }
      .modal-body-modern{ padding:30px; overflow-y:auto; flex:1; }

      
      .image-upload-section{ display:flex; justify-content:center; margin-bottom:32px; }
      .image-upload-wrapper{ cursor:pointer; transition: transform .2s; }
      .image-upload-wrapper:hover{ transform: scale(1.05); }
      .image-preview{ position:relative; width:110px; height:110px; }
      .preview-img{ width:100%; height:100%; object-fit:cover; border-radius:50%; border:4px solid #e2e8f0; transition: border-color .3s; }
      .image-upload-wrapper:hover .preview-img{ border-color: var(--brand); }
      .upload-overlay{
        position:absolute; bottom:8px; right:8px; width:32px; height:32px;
        background: var(--brand-dark); border-radius:50%;
        display:flex; align-items:center; justify-content:center; color:#fff;
        box-shadow: 0 2px 8px rgba(24,74,44,.35); transition: transform .2s;
      }
      .image-upload-wrapper:hover .upload-overlay{ transform: scale(1.1); }


      
      .form-group{ display:flex; flex-direction:column; }
      .form-group.full-width{ grid-column: 1 / -1; }
      .form-label-modern{ font-size:14px; font-weight:600; color:#374151; margin-bottom:8px; display:flex; align-items:center; gap:4px; }
      .form-input-modern{
        padding:12px 16px; border:2px solid #e2e8f0; border-radius:12px;
        font-size:14px; color:#1e293b; background:#fff; transition:.2s; outline:none;
      }
      .form-input-modern:focus{ border-color: var(--brand); box-shadow: 0 0 0 3px rgba(117,180,55,.2); }
      .form-input-modern::placeholder{ color:#9ca3af; }

      .checkbox-group{ display:flex; flex-wrap:wrap; gap:16px; margin-top:8px; }
      .checkbox-wrapper{ display:flex; align-items:center; cursor:pointer; user-select:none; }
      .checkbox-input{ width:18px; height:18px; margin:0 8px 0 0; cursor:pointer; accent-color: var(--brand-dark); }
      .checkbox-label{ font-size:14px; color:#374151; font-weight:500; }

      .modal-footer-modern{ padding:24px 30px; border-top:1px solid #e2e8f0; display:flex; gap:12px; justify-content:flex-end; background:#f8fafc; }
  .btn{ display:inline-flex; align-items:center; gap:8px; padding:12px 20px; border-radius:12px; font-weight:600; font-size:14px; border:none; cursor:pointer; transition:.2s; outline:none; position:relative; overflow:hidden; }
      .btn:disabled{ opacity:.6; cursor:not-allowed; }
      .btn-primary-modern{
        background: linear-gradient(135deg, var(--brand) 0%, var(--brand-dark) 100%);
        color:#fff; box-shadow: 0 4px 12px rgba(117,180,55,.35);
      }
      .btn-primary-modern:hover:not(:disabled){ transform: translateY(-2px); box-shadow: 0 8px 20px rgba(24,74,44,.45); }
      .btn-cancel-modern{ background:#f1f5f9; color:#64748b; border:2px solid #e2e8f0; }
      .btn-cancel-modern:hover{ background:#e2e8f0; border-color:#cbd5e1; }

      .tree-bg-modal .modal-container{
        width: 420px;
        max-width: 92vw;
      }
      .tree-bg-modal .modal-body-modern{
        display: grid;
        gap: 16px;
      }
      .color-preview-card{
        display:flex;
        align-items:center;
        gap:12px;
        padding:12px;
        border:1px solid #e2e8f0;
        border-radius:12px;
        background: #ffffff;
        box-shadow: inset 0 1px 0 rgba(255,255,255,.8);
      }
      .color-preview-swatch{
        width: 80px;
        height: 44px;
        border-radius: 10px;
        border: 1px solid #d1d5db;
        box-shadow: inset 0 1px 3px rgba(0,0,0,.08);
      }
      .color-preview-text{
        font-size: 13px;
        color: #475569;
        font-weight: 600;
        letter-spacing: .02em;
        text-transform: uppercase;
      }

      .loading-spinner{ animation: spin 1s linear infinite; }
      @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }


      @media (max-width: 640px){
        .form-grid{ grid-template-columns:1fr; }
        .modal-container{ margin:10px; width: calc(100vw - 20px); }
        .profile-modal .modal-container{ height:500px; }
        .compact-action-buttons{ padding:12px 16px; flex-direction:column; gap:8px; }
        .btn-compact{ width:100%; font-size:13px; padding:12px; }
        .modal-header-modern, .modal-body-modern, .modal-footer-modern{ padding:20px; }
        .overlay-name{ font-size:24px; }
        .checkbox-group{ flex-direction:column; gap:12px; }
        
        .modern-overlay {
          padding: 10px;
          align-items: flex-start;
          padding-top: 20px;
        }
        
        .modal-container { 
          margin: 0;
          width: 100%;
          max-height: calc(100vh - 40px);
        }
      }

      @media (max-width: 480px){
        .compact-action-buttons{ padding:10px 12px; }
        .btn-compact{ font-size:11px; padding:10px 8px; gap:4px; }
        .overlay-name{ font-size:20px; }
        .profile-modal .modal-container{ height:450px; }
      }

    
      :root { --footerbar-height: 64px; }
      .tree-wrapper{ position:relative; width:100%; height:600px; }

.add-family-modal .checkbox-group{
  display: flex !important;
  flex-direction: row !important;
  align-items: center;
  gap: 24px;
  flex-wrap: nowrap;
  white-space: nowrap;
}
.add-family-modal .checkbox-wrapper{
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.add-family-modal .checkbox-input{
  width: 16px;
  height: 16px;
  margin: 0;
  accent-color: var(--brand-dark);
}



    `}</style>



    </div>
  );
}

export default FamilySettingsTreeBackground;